"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.unregisterDirectionalInput = exports.registerDirectionalInput = exports.handleGamepadDisconnect = exports.clearDirectionalInputStack = exports.clearDirectionRepeats = void 0;
const gamepadEvents_1 = require("../../gamepadEvents");
const types_1 = require("../../../types");
const spatialNavigation_1 = require("../../navigation/spatialNavigation");
// Faster delay between key repeats after the user has held the key enough for directionRepeatThreshold
const REPEAT_KEY_DELAY_FAST_MS_DEFAULT = 300;
const REPEAT_KEY_DELAY_FAST_MS_HORIZONTAL = 100;
// Initial delay before repeating the same key if button is still held down
const REPEAT_KEY_DELAY_SLOW_MS_DEFAULT = 400;
const REPEAT_KEY_DELAY_SLOW_MS_HORIZONTAL = 200;
// The number of direction repeats before we speed up the key repeat to fast
const DIRECTION_REPEAT_THRESHOLD = 3;
// Delay before starting key repeat
const DELAY_UNTIL_KEY_REPEAT_MS = 50;
// Timers for repeating direction input when it's held down
let directionDelayTimer;
let directionRepeatInterval;
// The number of direction repeats during the slow speed
let directionRepeatCount = 0;
// Stack to keep track of input directions and sources.
let inputStack = [];
/**
 * Clears any ongoing input direction repetitions.
 * This is typically used to reset the state of repeated navigations.
 */
const clearDirectionRepeats = () => {
    clearTimeout(directionDelayTimer);
    clearInterval(directionRepeatInterval);
    directionRepeatCount = 0;
};
exports.clearDirectionRepeats = clearDirectionRepeats;
const clearDirectionalInputStack = () => {
    inputStack.length = 0;
};
exports.clearDirectionalInputStack = clearDirectionalInputStack;
/**
 * Handles the disconnection of a gamepad.
 * Removes all inputs associated with the disconnected gamepad from the input stack.
 * @param gamepadId - The identifier of the disconnected gamepad.
 */
const handleGamepadDisconnect = (gamepadId) => {
    const previousDirection = getCurrentFocusDirection();
    // Filter out all entries with the disconnected gamepadId
    inputStack = inputStack.filter((item) => item.source === types_1.DirectionalSource.ArrowKey || item.gamepadId !== gamepadId);
    const currentDirection = getCurrentFocusDirection();
    // Trigger directional input action if the focus direction has changed
    if (previousDirection !== currentDirection) {
        onDirectionalInput(currentDirection);
    }
};
exports.handleGamepadDisconnect = handleGamepadDisconnect;
/**
 * Registers a directional input into the input stack.
 * Prevents adding duplicate inputs (same direction, source, and gamepadId) to the stack.
 * @param direction - The direction of the input.
 * @param source - The source of the input.
 * @param gamepadId - The gamepad identifier.
 */
const registerDirectionalInput = (direction, source, gamepadId = 0) => {
    const previousDirection = getCurrentFocusDirection();
    // Check if the same input already exists in the stack
    const alreadyExists = inputStack.some((item) => item.direction === direction &&
        item.source === source &&
        (source !== types_1.DirectionalSource.ArrowKey ? item.gamepadId === gamepadId : true));
    if (alreadyExists) {
        return;
    }
    // Add the input to the stack if it's not already present
    const newItem = Object.assign({ direction,
        source }, (source !== types_1.DirectionalSource.ArrowKey && { gamepadId: gamepadId }));
    inputStack.push(newItem);
    // Trigger directional input action if the focus direction has changed
    if (previousDirection !== direction) {
        onDirectionalInput(direction);
    }
};
exports.registerDirectionalInput = registerDirectionalInput;
/**
 * Unregisters a directional input from the input stack.
 * @param direction - The direction of the input to be removed.
 * @param source - The source of the input to be removed.
 * @param gamepadId - The gamepad identifier.
 */
const unregisterDirectionalInput = (direction, source, gamepadId = 0) => {
    const previousDirection = getCurrentFocusDirection();
    // Find and remove the specified input from the stack
    const index = inputStack.findIndex((item) => item.direction === direction &&
        item.source === source &&
        (item.source !== types_1.DirectionalSource.ArrowKey ? item.gamepadId === gamepadId : true));
    if (index > -1) {
        inputStack.splice(index, 1);
    }
    // Trigger directional input action if the focus direction has changed
    const currentDirection = getCurrentFocusDirection();
    if (previousDirection !== currentDirection) {
        onDirectionalInput(currentDirection);
    }
};
exports.unregisterDirectionalInput = unregisterDirectionalInput;
/**
 * Retrieves the current focus direction based on the top of the input stack.
 * @returns The current focus direction or undefined if the stack is empty.
 */
const getCurrentFocusDirection = () => {
    if (inputStack.length > 0) {
        return inputStack[inputStack.length - 1].direction;
    }
};
/**
 * Handles directional input actions and sets up repetition behavior.
 * @param direction - The current focus direction to navigate.
 */
const onDirectionalInput = (direction) => {
    (0, exports.clearDirectionRepeats)();
    // If there's no direction, no further action is needed
    if (!direction) {
        return;
    }
    // Setting up repeating navigation based on the direction
    const isHorizontal = direction === types_1.FocusDirection.Left || direction === types_1.FocusDirection.Right;
    directionDelayTimer = setTimeout(() => {
        directionRepeatInterval = setInterval(() => {
            directionRepeatCount += 1;
            navigateWithEvent(direction);
            // After reaching a threshold, increase the repeat rate
            if (directionRepeatCount >= DIRECTION_REPEAT_THRESHOLD) {
                clearInterval(directionRepeatInterval);
                directionRepeatInterval = setInterval(() => {
                    navigateWithEvent(direction);
                }, isHorizontal ? REPEAT_KEY_DELAY_FAST_MS_HORIZONTAL : REPEAT_KEY_DELAY_FAST_MS_DEFAULT);
            }
        }, isHorizontal ? REPEAT_KEY_DELAY_SLOW_MS_HORIZONTAL : REPEAT_KEY_DELAY_SLOW_MS_DEFAULT);
    }, DELAY_UNTIL_KEY_REPEAT_MS);
    // Initial navigation in the specified direction
    navigateWithEvent(direction);
};
/**
 * Performs a spatial navigate in the given direction and emits a navigation event.
 *
 * @param direction - The direction to navigate.
 */
const navigateWithEvent = (direction) => {
    (0, spatialNavigation_1.spatialNavigate)(direction);
    let navigationEvent = gamepadEvents_1.NavigationEvent.NavigateUp;
    switch (direction) {
        case types_1.FocusDirection.Up:
            navigationEvent = gamepadEvents_1.NavigationEvent.NavigateUp;
            break;
        case types_1.FocusDirection.Down:
            navigationEvent = gamepadEvents_1.NavigationEvent.NavigateDown;
            break;
        case types_1.FocusDirection.Left:
            navigationEvent = gamepadEvents_1.NavigationEvent.NavigateLeft;
            break;
        case types_1.FocusDirection.Right:
            navigationEvent = gamepadEvents_1.NavigationEvent.NavigateRight;
    }
    gamepadEvents_1.GamepadEventEmitter.emit(navigationEvent);
};
//# sourceMappingURL=data:application/json;base64,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